import { logger } from '../../utils/logger';

export interface Signature {
  type?: string | string[];
  display?: string;
  location?: string | string[];
}

export interface Edition {
  type: string;
  count: number;
  total: number;
}

export interface RelatedWorkEntry {
  catalogItemId: string;
  title: string;
}

export type RelatedWork = string | RelatedWorkEntry;

export interface CreationDateData {
  type?: string;
  value?: string;
  display?: string;
  location?: string | string[];
}

const truncateForLog = (value: unknown): string => {
  const s = typeof value === 'string' ? value : String(value);
  return s.length > 100 ? `${s.slice(0, 100)}…` : s;
};

export const parseArrayField = (value: unknown): string => {
  if (!value) return '';
  try {
    const parsed =
      typeof value === 'string' && (value as string).includes('[')
        ? JSON.parse(value as string)
        : value;
    return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
  } catch (error) {
    logger.error('Failed to parse array field JSON; falling back to raw value', {
      error,
      rawValue: truncateForLog(value),
    });
    return String(value);
  }
};

export const parseSignatures = (raw: unknown): Signature[] => {
  if (!raw) return [];
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw as string) : raw;
    return Array.isArray(value) ? (value as Signature[]) : [];
  } catch (error) {
    logger.error('Failed to parse signatures JSON; signatures will be empty', {
      error,
      rawValue: truncateForLog(raw),
    });
    return [];
  }
};

export const flattenStrOrArray = (value: string | string[] | undefined): string => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .join(', ');
  }
  return String(value);
};

export const joinSignatureField = (
  signatures: Signature[],
  field: 'type' | 'display' | 'location',
): string => {
  const parts = signatures
    .map((s) => (field === 'display' ? (s.display ?? '') : flattenStrOrArray(s[field])))
    .filter(Boolean);
  return parts.join('; ');
};

export const formatEditions = (editions: Edition[] | undefined): string => {
  if (!editions || editions.length === 0) return '';
  return editions
    .filter((e) => e.type || e.count || e.total)
    .map((e) => `${e.type}: ${e.count}/${e.total}`)
    .join('; ');
};

export const flattenStepNotes = (
  src: Record<string, unknown> | null | undefined,
): string[] => {
  const raw = (src as { stepNotes?: Record<string, string[]> } | null | undefined)?.stepNotes;
  if (!raw) return [];
  return Object.values(raw).flat().filter((n) => typeof n === 'string' && n.trim() !== '');
};
